import { FloppyDiskIcon, ViewIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonSize } from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { Tabs } from "shared/components/Tabs";
import { useSurveyStore, selectCanPreview, toQuestionPayload } from "../store";
import { View } from "../constants";
import { Dispatch, SetStateAction, useState } from "react";
import { PreviewModal } from "../slices/preview-modal";
import { landlordSurveyApi } from "api/landlord-surveys/landlord-surveys";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const LayoutHeader = ({
  view,
  setView
}: {
  view: View;
  setView: Dispatch<SetStateAction<View>>;
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isCreateMode = id === "add";

  const data = useSurveyStore((state) => state.data);
  const isDirty = useSurveyStore((state) => state.isDirty);
  const dirtyFields = useSurveyStore((state) => state.dirtyFields);
  const resetApiData = useSurveyStore((state) => state.resetApiData);
  const canPreview = useSurveyStore(selectCanPreview);
  const [showPreview, setShowPreview] = useState(false);

  const createMutation = useMutation({
    mutationFn: () => {
      if (!data) throw new Error("No data to save");
      return landlordSurveyApi.createSurvey({
        name: data.name,
        is_default: data.is_default,
        questions: data.questions.map(toQuestionPayload)
      });
    },
    onSuccess: (result) => {
      resetApiData(result);
      queryClient.invalidateQueries({ queryKey: ["LANDLORD_SURVEYS"] });
      navigate(`/landlord-surveys/${result.identifier}`, { replace: true });
    }
  });

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!data?.identifier) throw new Error("No identifier");
      return landlordSurveyApi.updateSurvey(data.identifier, {
        identifier: data.identifier,
        dirty_map: [...dirtyFields],
        name: data.name,
        is_default: data.is_default,
        questions: data.questions.map(toQuestionPayload)
      });
    },
    onSuccess: (result) => {
      resetApiData(result);
      queryClient.invalidateQueries({ queryKey: ["LANDLORD_SURVEYS"] });
    }
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const handleSave = () => {
    if (isCreateMode) createMutation.mutate();
    else updateMutation.mutate();
  };

  const crumbs = [
    {
      title: "Surveys",
      href: "/landlord-surveys"
    },
    {
      title: data?.name || "Unknown",
      isActive: true
    }
  ];
  return (
    <>
      <header className="h-[72px] flex items-center justify-between border-b border-alpha-80 px-6">
        <BreadCrumbs crumbs={crumbs} />

        <Tabs
          tabs={[View.Questions, View.Conditions]}
          selected={view}
          setActiveTab={(t) => setView(t)}
        />

        <div className="flex gap-2">
          <Button
            HugeIcon={ViewIcon}
            size={ButtonSize.SM}
            disabled={!canPreview}
            tooltip={
              !canPreview ? "Add questions with titles to preview" : undefined
            }
            onClick={() => setShowPreview(true)}
          >
            Preview
          </Button>

          <Button
            HugeIcon={FloppyDiskIcon}
            size={ButtonSize.SM}
            disabled={!isDirty || isSaving}
            tooltip={isDirty ? undefined : "Make changes before saving"}
            onClick={handleSave}
          >
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </div>
      </header>
      {showPreview && <PreviewModal onClose={() => setShowPreview(false)} />}
    </>
  );
};
